// See Documentation: ../../../Document/QueueInsights/hooks/use-queue-charts.md
import { useMemo, useCallback } from "react";
import { QueueSQLData } from "../../types/queueTypes";
import { GeneratorDisplay } from "../map/GeneratorLayerColumns";
import { ISOFilterOptions } from "../constants/queueInsightsConstants";
import {
    PieChartDataFormat,
    BarChartDataFormat,
} from "../constants/chartConstants";
import QueueInsightsPieChart from "../charts/QueueInsightsPieChart";
import QueueInsightsBarChart from "../charts/QueueInsightsBarChart";
import { Loader2 } from "lucide-react";

type GraphDataType = {
    phase: PieChartDataFormat[][];
    isaQueues: PieChartDataFormat[];
    activeQueues: BarChartDataFormat[];
};

export interface UseQueueChartsProps {
    filteredIsoData: QueueSQLData[];
    operatingGeneratorsData: GeneratorDisplay[] | null;
    currentISOFilter: ISOFilterOptions;
    isLoading?: boolean;
    isFilterOptionsLoading?: boolean;
}

const ChartLoadingPlaceholder = ({ title }: { title: string }) => (
    <div className="h-[260px] flex flex-col items-center justify-center bg-white dark:bg-[#2D3748] rounded">
        <div className="flex flex-col items-center space-y-2">
            <Loader2 className="h-8 w-8 animate-spin stroke-black dark:stroke-white" />
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                Loading {title}...
            </p>
        </div>
    </div>
);

export const useQueueCharts = ({
    filteredIsoData,
    operatingGeneratorsData,
    currentISOFilter,
    isLoading = false,
    isFilterOptionsLoading = false,
}: UseQueueChartsProps) => {
    const quickFilter = useCallback(
        (entry: QueueSQLData, property: keyof QueueSQLData, value: string) => {
            const propertyValue = entry[property];
            return (
                propertyValue &&
                typeof propertyValue === "string" &&
                propertyValue.includes(value)
            );
        },
        []
    );

    const getPropertyCounts = useCallback(
        (phase: QueueSQLData[], property: keyof QueueSQLData) => {
            let solarCount = 0;
            let windCount = 0;
            let batteryCount = 0;
            let thermalCount = 0;
            let otherCount = 0;
            
            for (const entry of phase) {
                const fuelType = entry[property] as string;
                if (!fuelType || typeof fuelType !== "string") continue;

                switch (fuelType) {
                    case "Solar":
                        solarCount++;
                        break;
                    case "Wind":
                        windCount++;
                        break;
                    case "Battery":
                        batteryCount++;
                        break;
                    case "Thermal":
                        thermalCount++;
                        break;
                    case "Other":
                        otherCount++;
                        break;
                    default:
                        // Fallback for any unexpected fuel types
                        otherCount++;
                        break;
                }
            }
            return [solarCount, windCount, batteryCount, thermalCount, otherCount];
        },
        []
    );

    const calculateTotalMW = useCallback((data: QueueSQLData[]) => {
        return data.reduce((total, entry) => total + (entry.Size_MW || 0), 0);
    }, []);

    const memoizedCharts = useMemo(() => {
        const isMainDataLoading = isLoading || isFilterOptionsLoading;

        let processedOperatingGeneratorsData: PieChartDataFormat[] | null =
            null;
        {
            const data = operatingGeneratorsData;
            if (data) {
                const [solarCount, windCount, batteryCount, thermalCount, otherCount] =
                    getPropertyCounts(
                        data as unknown as QueueSQLData[],
                        "technology" as keyof QueueSQLData
                    );
                processedOperatingGeneratorsData = [
                    solarCount,
                    windCount,
                    batteryCount,
                    thermalCount,
                    otherCount,
                ].some((num) => num)
                    ? [
                          {
                              value: solarCount,
                              type: "solar",
                          },
                          {
                              value: windCount,
                              type: "wind",
                          },
                          {
                              value: batteryCount,
                              type: "battery",
                          },
                          {
                              value: thermalCount,
                              type: "thermal",
                          },
                          {
                              value: otherCount,
                              type: "other",
                          },
                      ]
                    : null;
            }
        }

        let phaseGraphs: GraphDataType | null = null;
        let activeResult = [] as BarChartDataFormat[];
        let phase1TotalMW = 0;
        let phase2TotalMW = 0;
        let phase3TotalMW = 0;
        let isaTotalMW = 0;

        if (!isMainDataLoading) {
            const data = filteredIsoData;
            if (data) {
                const currentISOFilterLoc: string = currentISOFilter ?? "";
                const phase1 = data.filter(
                    (entry) =>
                        quickFilter(entry, "Study_Phase", "Phase 1") &&
                        quickFilter(entry, "ISO", currentISOFilterLoc)
                );
                const phase2 = data.filter(
                    (entry) =>
                        quickFilter(entry, "Study_Phase", "Phase 2") &&
                        quickFilter(entry, "ISO", currentISOFilterLoc)
                );
                const phase3 = data.filter(
                    (entry) =>
                        quickFilter(entry, "Study_Phase", "Phase 3") &&
                        quickFilter(entry, "ISO", currentISOFilterLoc)
                );
                const isaphase = data.filter(
                    (entry) =>
                        quickFilter(entry, "Study_Phase", "IA Signed") &&
                        quickFilter(entry, "ISO", currentISOFilterLoc)
                );
                
                phase1TotalMW = calculateTotalMW(phase1);
                phase2TotalMW = calculateTotalMW(phase2);
                phase3TotalMW = calculateTotalMW(phase3);
                isaTotalMW = calculateTotalMW(isaphase);

                if (currentISOFilter) {
                    // defines what to filter by
                    const activeLabelMap: Map<string, QueueSQLData[]> =
                        new Map();
                    data.forEach((entry) => {
                        const queueCycleKey = String(entry.Queue_Cycle);
                        if (activeLabelMap.has(queueCycleKey)) {
                            activeLabelMap.get(queueCycleKey)!.push(entry);
                        } else {
                            activeLabelMap.set(queueCycleKey, [entry]);
                        }
                    });
                    activeResult = Array.from(activeLabelMap.entries()).map(
                        (entry: [string, QueueSQLData[]]) => {
                            const label = entry[0];
                            const [solar, wind, battery, thermal, other] =
                                getPropertyCounts(entry[1], "Fuel_Type");
                            return {
                                label,
                                solar,
                                wind,
                                battery,
                                thermal,
                                other,
                            };
                        }
                    );
                }
                const phases = [phase1, phase2, phase3, isaphase];
                const res = phases.map((phase) => {
                    const [solarCount, windCount, batteryCount, thermalCount, otherCount] =
                        getPropertyCounts(phase, "Fuel_Type");
                    return [
                        solarCount,
                        windCount,
                        batteryCount,
                        thermalCount,
                        otherCount,
                    ].some((num) => num)
                        ? [
                              {
                                  value: solarCount,
                                  type: "solar",
                              },
                              {
                                  value: windCount,
                                  type: "wind",
                              },
                              {
                                  value: batteryCount,
                                  type: "battery",
                              },
                              {
                                  value: thermalCount,
                                  type: "thermal",
                              },
                              {
                                  value: otherCount,
                                  type: "other",
                              },
                          ]
                        : null;
                });

                phaseGraphs = {
                    phase: [res[0], res[1], res[2]],
                    isaQueues: res[3],
                } as GraphDataType;
            }
        }

        return (
            <div className="grid grid-cols-3 auto-rows-[minmax(260px,1fr)] gap-2 w-1/2 h-full min-h-0 overflow-y-auto">
                {isMainDataLoading ? (
                    <ChartLoadingPlaceholder title="Phase I Queues" />
                ) : (
                    <QueueInsightsPieChart
                        {...{
                            data: phaseGraphs ? phaseGraphs.phase[0] ?? [] : [],
                            title: "Phase I Queues",
                            summary: Math.round(phase1TotalMW / 1000 * 100) / 100,
                        }}
                    />
                )}
                {isMainDataLoading ? (
                    <ChartLoadingPlaceholder title="Phase II Queues" />
                ) : (
                    <QueueInsightsPieChart
                        {...{
                            data: phaseGraphs ? phaseGraphs.phase[1] ?? [] : [],
                            title: "Phase II Queues",
                            summary: Math.round(phase2TotalMW / 1000 * 100) / 100,
                        }}
                    />
                )}
                {isMainDataLoading ? (
                    <ChartLoadingPlaceholder title="Phase III Queues" />
                ) : (
                    <QueueInsightsPieChart
                        {...{
                            data: phaseGraphs ? phaseGraphs.phase[2] ?? [] : [],
                            title: "Phase III Queues",
                            summary: Math.round(phase3TotalMW / 1000 * 100) / 100,
                        }}
                    />
                )}
                {isMainDataLoading ? (
                    <ChartLoadingPlaceholder title="ISA Signed Queues" />
                ) : (
                    <QueueInsightsPieChart
                        {...{
                            data: phaseGraphs?.isaQueues ?? [],
                            title: "ISA Signed Queues",
                            summary: Math.round(isaTotalMW / 1000 * 100) / 100,
                        }}
                    />
                )}
                {!operatingGeneratorsData ? (
                    <ChartLoadingPlaceholder title="Operating Generators" />
                ) : (
                    <QueueInsightsPieChart
                        {...{
                            data: processedOperatingGeneratorsData ?? [],
                            title: "Operating Generators",
                        }}
                    />
                )}
                {isMainDataLoading ? (
                    <ChartLoadingPlaceholder title="Active Queues by Cycle" />
                ) : (
                    <QueueInsightsBarChart
                        {...{
                            data: activeResult ?? [],
                            title: "Active Queues by Cycle",
                            textOverride:
                                currentISOFilter ??
                                "Select an ISO filter to display data",
                        }}
                    />
                )}
            </div>
        );
    }, [
        currentISOFilter,
        getPropertyCounts,
        filteredIsoData,
        operatingGeneratorsData,
        quickFilter,
        isLoading,
        isFilterOptionsLoading,
        calculateTotalMW,
    ]);

    return memoizedCharts;
};
